/**
 * PhotorealCity — Google Photorealistic 3D Tiles
 * ---------------------------------------------------------------------
 * Streams Google's photogrammetric 3D mesh of LA into the scene via
 * 3d-tiles-renderer. Two plugins:
 *   • GoogleCloudAuthPlugin — handles the session-token caching that
 *     Google's Map Tiles API requires.
 *   • ReorientationPlugin   — rotates the entire tileset so that
 *     (PARKER_LAT, PARKER_LNG) lands at world origin with +Y up.
 *     This keeps the camera in plain Three.js coordinates instead of
 *     ECEF (where world origin = centre of the Earth).
 *
 * Note: ReorientationPlugin's lat/lon are in RADIANS. Easy to miss.
 *
 * Costs: Google's $200/month Map Platform credit covers ~28k tile
 * requests/month. Beyond that they bill.
 */
import React, { useRef, useEffect, Suspense } from 'react';
import { useThree } from '@react-three/fiber';
import {
  TilesRenderer,
  TilesPlugin,
  TilesAttributionOverlay,
} from '3d-tiles-renderer/r3f';
import { Billboard, Text, Sky, Cloud, Clouds, Environment } from '@react-three/drei';
import {
  GoogleCloudAuthPlugin,
  ReorientationPlugin,
  TilesFadePlugin,
  TileCompressionPlugin,
} from '3d-tiles-renderer/plugins';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const ACCENT = '#C9A96E';

/* ── Drop-pin marker for The Parker ────────────────────────────────
 *  Real Apple/Google-Maps-style dropped pin: a cone pointing down
 *  to the exact ground location at world origin (= Parker's lat/lng
 *  after the ReorientationPlugin rotates the tiles), a head sphere
 *  above the cone, a vertical post rising into the sky, and a
 *  billboarded "THE PARKER" label that always faces the camera.
 *  Bobs gently and pulses opacity so it's findable from any angle.
 */
function ParkerPin() {
  const headRef  = useRef<THREE.Group>(null);
  const haloRef  = useRef<THREE.Mesh>(null);
  const labelRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (headRef.current) {
      headRef.current.position.y = 32 + Math.sin(t * 1.6) * 1.5;
    }
    if (labelRef.current) {
      labelRef.current.position.y = 50 + Math.sin(t * 1.6) * 1.5;
    }
    if (haloRef.current) {
      const m = haloRef.current.material as THREE.MeshBasicMaterial;
      m.opacity = 0.18 + Math.sin(t * 2.2) * 0.10;
    }
  });

  return (
    <>
      {/* Vertical post — sticks up out of the building so the pin
          is visible from far away and the eye traces it down to a
          single ground spot. */}
      <mesh position={[0, 16, 0]}>
        <cylinderGeometry args={[0.35, 0.35, 32, 8]} />
        <meshStandardMaterial color={ACCENT} emissive={ACCENT} emissiveIntensity={0.6} />
      </mesh>

      {/* Drop-pin cone pointing DOWN to ground origin */}
      <mesh position={[0, 4, 0]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[2.2, 8, 16]} />
        <meshStandardMaterial color={ACCENT} emissive={ACCENT} emissiveIntensity={0.7}
          roughness={0.35} metalness={0.5} />
      </mesh>

      {/* Floating head sphere — bobs above the post */}
      <group ref={headRef} position={[0, 32, 0]}>
        <mesh>
          <sphereGeometry args={[3.0, 24, 18]} />
          <meshStandardMaterial
            color={ACCENT}
            emissive={ACCENT}
            emissiveIntensity={1.1}
            roughness={0.35}
            metalness={0.55}
          />
        </mesh>
        {/* Halo */}
        <mesh ref={haloRef}>
          <sphereGeometry args={[5.5, 20, 14]} />
          <meshBasicMaterial color={ACCENT} transparent opacity={0.18} depthWrite={false} />
        </mesh>
      </group>

      {/* Floating "THE PARKER" label that always faces the camera.
          Text suspends while its font file streams from a CDN — wrap it
          in its own Suspense so a slow/blocked font fetch can't blank
          the entire scene under the parent Suspense. */}
      <group ref={labelRef} position={[0, 50, 0]}>
        <Billboard>
          {/* Dark backing plate so the text reads against any sky */}
          <mesh>
            <planeGeometry args={[18, 5]} />
            <meshBasicMaterial color="#0a1322" transparent opacity={0.85} depthWrite={false} />
          </mesh>
          <Suspense fallback={null}>
            <Text
              position={[0, 0, 0.05]}
              fontSize={2.4}
              color={ACCENT}
              anchorX="center"
              anchorY="middle"
              letterSpacing={0.08}>
              THE PARKER
            </Text>
          </Suspense>
        </Billboard>
      </group>

      {/* Soft ground halo at world origin so the user can spot the
          exact building footprint. Renders just above ground level. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.5, 0]}>
        <ringGeometry args={[6, 9, 32]} />
        <meshBasicMaterial color={ACCENT} transparent opacity={0.6} depthWrite={false} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.4, 0]}>
        <circleGeometry args={[6, 32]} />
        <meshBasicMaterial color={ACCENT} transparent opacity={0.20} depthWrite={false} />
      </mesh>
    </>
  );
}

interface PhotorealCityProps {
  apiKey:    string;
  anchorLat: number;          // degrees
  anchorLng: number;          // degrees
  height?:   number;          // metres above ellipsoid (default 0)
}

/* ── Midday LA sky + atmospheric haze ──────────────────────────────
 *  Bright blue Hosek-Wilkie sky with the sun ~30° from zenith in the
 *  south-west. Scene background set to the same hazy blue so anywhere
 *  the tiles haven't loaded yet reads as sky rather than the cold
 *  navy default. Fog pushes the far horizon into a soft blue haze.
 */
function MiddaySky() {
  const { scene } = useThree();
  useEffect(() => {
    scene.background = new THREE.Color('#a8c5e3');
    scene.fog = new THREE.Fog('#bfd4ee', 1200, 7500);
    return () => {
      scene.background = null;
      scene.fog = null;
    };
  }, [scene]);

  // Sun position derived from inclination=0.18 (32° from zenith),
  // azimuth=0.28 (south-west). Same convention as the stylized scene.
  const sunPhi   = 0.18 * Math.PI;
  const sunTheta = 0.28 * 2 * Math.PI;
  const sunPos: [number, number, number] = [
    Math.sin(sunPhi) * Math.cos(sunTheta),
    Math.cos(sunPhi),
    Math.sin(sunPhi) * Math.sin(sunTheta),
  ];
  return (
    <>
      <Sky
        distance={45000}
        sunPosition={sunPos}
        inclination={0.18}
        azimuth={0.28}
        rayleigh={1.4}
        turbidity={10}
        mieCoefficient={0.005}
        mieDirectionalG={0.85}
      />
      {/* Image-based lighting from an outdoor city HDR — gives every
          surface a richer ambient + subtle highlight reflection. Doesn't
          replace the directional sun, just fills in the dark sides of
          buildings the way real skylight does. Wrapped in its own
          Suspense so a slow / blocked HDR fetch can't blank the entire
          scene under the parent Suspense — the leading cause of the
          "page won't render until DevTools opens" cold-start bug. */}
      <Suspense fallback={null}>
        <Environment preset="city" background={false} environmentIntensity={0.55} />
      </Suspense>
      {/* A handful of high-altitude cumulus clouds. Read from any
          aerial angle but stay clear of the camera at street level. */}
      <Clouds material={THREE.MeshBasicMaterial}>
        <Cloud
          seed={1}
          segments={42}
          bounds={[260, 30, 260]}
          volume={70}
          color="#ffffff"
          opacity={0.62}
          fade={6000}
          position={[0, 600, -900]}
        />
        <Cloud
          seed={2}
          segments={42}
          bounds={[220, 25, 220]}
          volume={60}
          color="#f4f5f7"
          opacity={0.55}
          fade={6000}
          position={[1200, 660, 800]}
        />
        <Cloud
          seed={3}
          segments={38}
          bounds={[260, 28, 260]}
          volume={70}
          color="#ffffff"
          opacity={0.6}
          fade={6000}
          position={[-1500, 700, -1200]}
        />
        <Cloud
          seed={4}
          segments={36}
          bounds={[180, 20, 180]}
          volume={45}
          color="#eef0f4"
          opacity={0.45}
          fade={6000}
          position={[800, 580, -1700]}
        />
        <Cloud
          seed={5}
          segments={36}
          bounds={[200, 22, 200]}
          volume={50}
          color="#ffffff"
          opacity={0.5}
          fade={6000}
          position={[-900, 640, 1400]}
        />
      </Clouds>
    </>
  );
}


/* One-shot camera + controls focus on the anchor when Photoreal mounts. */
function PhotorealCameraInit() {
  const { camera, controls } = useThree() as any;
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !controls) return;
    // Closer/lower than the stylized default so Parker fills more of
    // the frame. Roughly 3-block radius from a 30°-ish elevation.
    camera.position.set(110, 90, 110);
    controls.target.set(0, 0, 0);
    controls.update();
    done.current = true;
  }, [camera, controls]);
  return null;
}

export default function PhotorealCity({
  apiKey, anchorLat, anchorLng, height = 0,
}: PhotorealCityProps) {
  const DEG = Math.PI / 180;

  return (
    <>
      <PhotorealCameraInit />
      <MiddaySky />
      {/* Lighting: bright ambient + warm sun. The tile mesh comes pre-
          baked with photogrammetric textures so we don't need PBR
          intensity, just enough light to show the textures. */}
      <ambientLight intensity={0.7} color="#cfe2ff" />
      <directionalLight position={[400, 600, 400]} intensity={1.2} color="#fff4d8" />

      {/* TilesRenderer tuning:
            - errorTarget 6→3.5: refines tiles more aggressively so the
              mesh stays sharp at any zoom. Costs more tile requests
              but the visual jump is significant.
            - errorThreshold caps the per-tile screen-space error so we
              don't fall back to muddy LOD0 in the far distance. */}
      {/* errorTarget = 1 is essentially "refine to the highest LOD
          Google publishes". Trees stop being blob silhouettes, road
          markings + storefront text become legible. Costs more
          bandwidth per tile request — still inside Google's free
          credit at our usage. */}
      <TilesRenderer
        url="https://tile.googleapis.com/v1/3dtiles/root.json"
        errorTarget={1}
        errorThreshold={30}>
        <TilesPlugin
          plugin={GoogleCloudAuthPlugin}
          args={[{ apiToken: apiKey, autoRefreshToken: true }]}
        />
        <TilesPlugin
          plugin={ReorientationPlugin}
          args={[{
            // Reorient the whole tileset so the Parker's surface point
            // sits at world origin with the local up direction = +Y.
            // RADIANS, not degrees.
            lat: anchorLat * DEG,
            lon: anchorLng * DEG,
            height,
            recenter: true,
          }]}
        />
        {/* Crossfade tiles when LOD swaps — eliminates pop-in flickers
            as you zoom or pan, scene reads as continuous. */}
        <TilesPlugin
          plugin={TilesFadePlugin}
          args={[{ duration: 250, fadeRootTiles: true }]}
        />
        {/* Decompresses draco/ktx2 mesh + texture compression. Same
            geometry, dramatically smaller transfer, cleaner textures. */}
        <TilesPlugin plugin={TileCompressionPlugin} />
      </TilesRenderer>

      {/* Parker pin floats above world origin = on top of The Parker
          in the photogrammetric mesh. */}
      <ParkerPin />

      {/* Mandatory Google attribution overlay. */}
      <TilesAttributionOverlay
        style={{
          position: 'absolute',
          bottom: 6, left: 12,
          color: '#FFFFFF',
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '0.02em',
          textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}
